// PublicFAQPage.js - Page FAQ publique (sans DashboardLayout)
import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  User,
  MessageSquare,
  Zap,
  Crown,
  Stethoscope,
  Mail,
  Sparkles,
} from 'lucide-react';

// Catégories et questions
const FAQ_DATA = [
  {
    category: 'Compte & Données',
    icon: User,
    color: 'blue',
    questions: [
      {
        id: 'delete-account',
        question: 'Comment supprimer mon compte ?',
        answer: `Pour supprimer votre compte :

1. Allez dans **Profil** (icône en haut à droite)
2. Cliquez sur **"Paramètres"**
3. Cliquez sur **"Supprimer mon compte"**
4. Confirmez la suppression

⚠️ **Attention** : Cette action est irréversible. Toutes vos données (conversations, analyses, pharmacie) seront définitivement supprimées.`,
      },
      {
        id: 'delete-conversations',
        question: 'Comment supprimer mes conversations ?',
        answer: `Vous avez plusieurs options :

**Supprimer une conversation spécifique :**
1. Allez dans **Historique**
2. Survolez la conversation à supprimer
3. Cliquez sur l'icône **poubelle** 🗑️

**Supprimer tout l'historique :**
1. Allez dans **Profil** → **Paramètres**
2. Cliquez sur **"Effacer l'historique"**

Les conversations supprimées ne peuvent pas être récupérées.`,
      },
      {
        id: 'data-security',
        question: 'Mes données sont-elles sécurisées ?',
        answer: `Oui, nous prenons la sécurité très au sérieux :

🔒 **Chiffrement** : Vos messages sont chiffrés avec AES-256-GCM
🔐 **Clés uniques** : Chaque utilisateur a sa propre clé de chiffrement
🇪🇺 **RGPD** : Nous respectons la réglementation européenne
🚫 **Pas de revente** : Vos données ne sont jamais vendues à des tiers

Vos données de santé sont traitées avec le plus grand soin conformément aux normes en vigueur.`,
      },
      {
        id: 'data-retention',
        question: 'Combien de temps mes données sont conservées ?',
        answer: `**Durée de conservation :**

• **Conversations** : 2 ans maximum, puis suppression automatique
• **Analyses de sang** : 2 ans maximum
• **Compte** : Jusqu'à suppression par vos soins

Vous pouvez supprimer vos données à tout moment depuis votre profil.

Après suppression de votre compte, toutes les données sont effacées sous 30 jours.`,
      },
    ],
  },
  {
    category: 'Fonctionnalités',
    icon: Zap,
    color: 'purple',
    questions: [
      {
        id: 'no-resume-conversation',
        question: 'Pourquoi je ne peux pas reprendre une ancienne conversation ?',
        answer: `C'est une **décision de sécurité médicale**.

**Le problème :**
Votre état de santé évolue avec le temps. Si vous reprenez une conversation d'il y a 2 semaines, l'IA pourrait :
• Ne pas savoir si vos symptômes ont évolué
• Donner des conseils basés sur des informations obsolètes
• Manquer une aggravation ou une amélioration

**Notre solution :**
Chaque nouvelle conversation repart de zéro pour que l'IA ait toujours les informations les plus récentes sur votre état.

💡 **Conseil** : Si vous avez un suivi à faire, décrivez à nouveau vos symptômes actuels et mentionnez l'évolution depuis la dernière fois.`,
      },
      {
        id: 'ai-slow',
        question: "Pourquoi l'IA met du temps à répondre ?",
        answer: `L'IA prend **6-8 secondes** en moyenne. Voici pourquoi :

**Ce qui se passe en coulisses :**
1. 🔍 **Analyse de sécurité** : Détection d'urgences vitales
2. 🧠 **Génération** : L'IA réfléchit à la meilleure réponse
3. ✅ **Validation** : Vérification que la réponse est sûre

**Pourquoi c'est important :**
Ces étapes garantissent que vous recevez une réponse **fiable et sécurisée**, pas juste rapide.

💡 En cas d'urgence vitale détectée, la réponse est immédiate avec les instructions d'appeler le 15 (SAMU).`,
      },
      {
        id: 'free-limit',
        question: "C'est quoi la limite de recherches gratuites ?",
        answer: `**Utilisateurs gratuits :**
• **3 recherches par jour** (chat IA + recherche médicaments)
• L'aide quotidienne (quiz santé) est **illimitée**
• Le compteur se réinitialise automatiquement après 24h

**Utilisateurs Premium :**
• **Recherches illimitées** 🚀
• Accès à toutes les fonctionnalités

Vous pouvez voir votre quota restant après chaque message.`,
      },
      {
        id: 'blood-analysis',
        question: "Comment fonctionne l'analyse de sang ?",
        answer: `**Étapes :**
1. 📸 **Uploadez** une photo de vos résultats d'analyse
2. 🔍 **L'IA lit et explique** les valeurs en langage simple
3. 📊 **Vous recevez** une explication claire avec code couleur :
   • 🟢 **Dans l'intervalle indiqué**
   • 🔵 **Hors de l'intervalle indiqué**

⚠️ **Important** : Cette analyse est indicative. Seul votre médecin peut interpréter vos résultats.

💡 L'analyse est sauvegardée dans votre historique pour référence.`,
      },
      {
        id: 'new-conversation',
        question: 'Quand une nouvelle conversation est-elle créée ?',
        answer: `Une nouvelle conversation est créée automatiquement :

• ⏱️ Après **30 minutes d'inactivité**
• 🔄 Quand vous cliquez sur **"Nouvelle conversation"**

**Pourquoi ?**
Cela permet de bien séparer vos différentes questions de santé dans l'historique et d'avoir des conversations organisées.

Les conversations précédentes restent consultables dans l'**Historique**.`,
      },
    ],
  },
  {
    category: 'Médical & Sécurité',
    icon: Stethoscope,
    color: 'red',
    questions: [
      {
        id: 'replace-doctor',
        question: "L'IA peut-elle remplacer un médecin ?",
        answer: `**Non, absolument pas.**

Remadia est un **assistant d'information**, pas un médecin :

❌ **Ne remplace PAS** :
• Un diagnostic médical
• Une consultation professionnelle
• Un traitement prescrit
• Un avis médical personnalisé

✅ **Peut vous aider à** :
• Comprendre des termes médicaux
• Avoir des informations générales
• Savoir quand consulter
• Mieux préparer vos questions pour le médecin

⚠️ **En cas de doute, consultez toujours un professionnel de santé.**`,
      },
      {
        id: 'emergency',
        question: "Que faire en cas d'urgence ?",
        answer: `🚨 **EN CAS D'URGENCE VITALE :**

**Appelez immédiatement le 15 (SAMU)**

**Signes d'urgence vitale :**
• Douleur thoracique intense
• Difficulté à respirer
• Perte de connaissance
• Saignement important
• Paralysie soudaine
• Confusion brutale

**Notre IA détecte ces situations** et vous redirigera immédiatement vers le 15.

⚠️ **N'attendez JAMAIS** une réponse de l'IA si vous pensez être en danger. Appelez le 15 directement.`,
      },
      {
        id: 'reliability',
        question: 'Les informations sont-elles fiables ?',
        answer: `**Nos sources et garanties :**

✅ **Informations générales** basées sur des connaissances médicales établies
✅ **Mises à jour régulières** du modèle d'IA
✅ **Système de sécurité** pour détecter les urgences
✅ **Validation** des réponses avant affichage

⚠️ **Limites :**
• L'IA peut faire des erreurs
• Les informations sont générales, pas personnalisées
• Votre médecin connaît votre dossier, pas l'IA

💡 **Notre conseil** : Utilisez Remadia pour vous informer, mais consultez un professionnel pour toute décision médicale.`,
      },
      {
        id: 'children',
        question: 'Puis-je utiliser Remadia pour mes enfants ?',
        answer: `**Oui, avec prudence :**

✅ Vous pouvez poser des questions sur la santé de vos enfants
✅ L'IA adapte ses réponses pour les populations vulnérables
✅ Elle recommandera plus facilement de consulter un médecin

⚠️ **Important :**
• Les enfants (surtout les nourrissons) nécessitent une vigilance accrue
• En cas de doute, consultez rapidement un pédiatre
• Les symptômes chez l'enfant peuvent évoluer rapidement

🚨 **Pour les nourrissons** : Consultez un médecin au moindre doute.`,
      },
    ],
  },
  {
    category: 'Premium & Abonnement',
    icon: Crown,
    color: 'yellow',
    questions: [
      {
        id: 'premium-benefits',
        question: 'Quels sont les avantages Premium ?',
        answer: `**Avantages Premium :**

🚀 **Recherches illimitées** : Plus de limite quotidienne
💊 **Ma Pharmacie** : Gérez vos médicaments avec informations détaillées
📄 **Export PDF** : Exportez votre pharmacie en PDF
⚡ **Priorité** : Accès prioritaire aux nouvelles fonctionnalités
🔍 **Analyse de sang** : Lecture vulgarisée et détaillée sur vos analyses`,
      },
      {
        id: 'cancel-subscription',
        question: 'Comment annuler mon abonnement ?',
        answer: `**Pour annuler votre abonnement Premium :**

1. Allez dans **Profil**
2. Section **"Abonnement"**
3. Cliquez sur **"Gérer mon abonnement"**
4. Suivez les instructions pour annuler

**Bon à savoir :**
• Votre accès Premium reste actif jusqu'à la fin de la période payée
• Aucun remboursement au prorata
• Vous pouvez vous réabonner à tout moment
• Vos données ne sont pas supprimées

📧 En cas de problème, contactez-nous à **contact@remadia.fr**`,
      },
      {
        id: 'payment-methods',
        question: 'Quels moyens de paiement sont acceptés ?',
        answer: `**Moyens de paiement acceptés :**

💳 **Cartes bancaires** :
• Visa
• Mastercard
• American Express

🔒 **Sécurité** :
• Paiement sécurisé via Stripe
• Vos données bancaires ne sont jamais stockées chez nous
• Conformité PCI-DSS

Les paiements sont traités de manière sécurisée par notre partenaire Stripe.`,
      },
    ],
  },
  {
    category: 'Contact & Support',
    icon: Mail,
    color: 'green',
    questions: [
      {
        id: 'contact',
        question: 'Comment contacter le support ?',
        answer: `**Plusieurs moyens de nous contacter :**

📧 **Email général** : contact@remadia.fr
🔒 **Protection des données** : dpo@remadia.fr
🐛 **Signaler un bug** : Utilisez le bouton "Feedback" dans l'application

**Délai de réponse** : Nous répondons généralement sous 48h ouvrées.

💡 **Conseil** : Décrivez votre problème le plus précisément possible pour une réponse plus rapide.`,
      },
      {
        id: 'bug-report',
        question: 'Comment signaler un bug ?',
        answer: `**Pour signaler un bug :**

1. Cliquez sur le bouton **"Feedback"** (en bas de page)
2. Sélectionnez **"Bug"** comme sujet
3. Décrivez le problème :
   • Ce que vous faisiez
   • Ce qui s'est passé
   • Ce que vous attendiez
4. Envoyez !

📸 **Si possible** : Faites une capture d'écran du problème.

Nous traitons tous les rapports de bugs et vous tiendrons informé de la résolution.`,
      },
      {
        id: 'feature-request',
        question: 'Comment suggérer une fonctionnalité ?',
        answer: `**Vos idées nous intéressent !**

1. Cliquez sur **"Feedback"**
2. Sélectionnez **"Suggestion"**
3. Décrivez votre idée

**Ce qu'on aime savoir :**
• Quel problème cette fonctionnalité résoudrait ?
• Comment l'utiliseriez-vous ?

Nous lisons toutes les suggestions et les plus populaires sont priorisées dans notre roadmap. 🚀`,
      },
    ],
  },
];

// Couleurs par catégorie
const CATEGORY_COLORS = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-950/30',
    border: 'border-purple-200 dark:border-purple-800',
    icon: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-100 dark:bg-purple-900/50',
  },
  red: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-100 dark:bg-red-900/50',
  },
  yellow: {
    bg: 'bg-yellow-50 dark:bg-yellow-950/30',
    border: 'border-yellow-200 dark:border-yellow-800',
    icon: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-100 dark:bg-yellow-900/50',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-950/30',
    border: 'border-green-200 dark:border-green-800',
    icon: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-100 dark:bg-green-900/50',
  },
};

const PublicFAQPage = () => {
  const navigate = useNavigate();
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleQuestion = (questionId) => {
    setExpandedQuestion(expandedQuestion === questionId ? null : questionId);
  };

  const filteredFAQ = FAQ_DATA.map((category) => ({
    ...category,
    questions: category.questions.filter(
      (q) =>
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.questions.length > 0);

  const totalQuestions = FAQ_DATA.reduce((acc, cat) => acc + cat.questions.length, 0);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors mb-6"
        >
          ← Retour
        </button>
        {/* Header avec logo */}
        <div className="text-center mb-12">
          <img src="/transparent-logo.png" alt="Remadia AI" className="w-60 h-60 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Questions Fréquentes
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {totalQuestions} questions pour vous aider à utiliser Remadia
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher une question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-5 py-4 pl-12 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 text-gray-900 dark:text-gray-100 text-lg"
            />
            <HelpCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* Catégories et Questions */}
        {filteredFAQ.length === 0 ? (
          <Card className="p-12 text-center bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800">
            <HelpCircle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Aucun résultat
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Essayez avec d'autres mots-clés ou contactez-nous directement.
            </p>
          </Card>
        ) : (
          <div className="space-y-8">
            {filteredFAQ.map((category) => {
              const colors = CATEGORY_COLORS[category.color];
              const Icon = category.icon;

              return (
                <div key={category.category}>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-xl ${colors.iconBg} flex items-center justify-center`}
                    >
                      <Icon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {category.category}
                    </h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({category.questions.length})
                    </span>
                  </div>

                  <div className="space-y-3">
                    {category.questions.map((q) => (
                      <Card
                        key={q.id}
                        className={`overflow-hidden border-2 ${colors.border} transition-all ${
                          expandedQuestion === q.id ? colors.bg : 'bg-white dark:bg-gray-900'
                        }`}
                      >
                        <button
                          onClick={() => toggleQuestion(q.id)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left"
                        >
                          <span className="font-semibold text-gray-900 dark:text-gray-100 pr-4">
                            {q.question}
                          </span>
                          {expandedQuestion === q.id ? (
                            <ChevronUp className={`w-5 h-5 ${colors.icon} flex-shrink-0`} />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>

                        {expandedQuestion === q.id && (
                          <div className="px-6 pb-5 pt-0 border-t border-gray-100 dark:border-gray-800">
                            <div className="mt-4 prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-gray-300">
                              {q.answer.split('\n').map((line, index) => {
                                if (line.startsWith('**') && line.endsWith('**')) {
                                  return (
                                    <h4
                                      key={index}
                                      className="font-bold text-gray-900 dark:text-gray-100 mt-4 mb-2"
                                    >
                                      {line.replace(/\*\*/g, '')}
                                    </h4>
                                  );
                                }
                                if (line.startsWith('•') || line.startsWith('-')) {
                                  return (
                                    <p key={index} className="flex items-start gap-2 my-1">
                                      <span className="text-blue-500 mt-1">•</span>
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: DOMPurify.sanitize(
                                            line
                                              .replace(/^[•-]\s*/, '')
                                              .replace(
                                                /\*\*(.*?)\*\*/g,
                                                '<strong class="text-gray-900 dark:text-gray-100">$1</strong>'
                                              ),
                                            {
                                              ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a'],
                                              ALLOWED_ATTR: ['href', 'target', 'rel']
                                            }
                                          ),
                                        }}
                                      />
                                    </p>
                                  );
                                }
                                if (line.trim() === '') {
                                  return <br key={index} />;
                                }
                                return (
                                  <p
                                    key={index}
                                    className="my-2"
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(
                                        line.replace(
                                          /\*\*(.*?)\*\*/g,
                                          '<strong class="text-gray-900 dark:text-gray-100">$1</strong>'
                                        ),
                                        {
                                          ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a'],
                                          ALLOWED_ATTR: ['href', 'target', 'rel']
                                        }
                                      ),
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Inscription */}
        <Card className="mt-12 p-8 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-2 border-cyan-200 dark:border-cyan-800 text-center">
          <Sparkles className="w-10 h-10 text-cyan-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Prêt à essayer Remadia ?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">
            Chat IA santé, analyse de prise de sang, recherche de médicaments et bien plus encore.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-3 rounded-xl text-lg"
            >
              Créer un compte gratuit
              <Sparkles className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-gray-300 dark:border-gray-600 px-8 py-3 rounded-xl"
            >
              Se connecter
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PublicFAQPage;